import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { Check, Minus, Plus } from "lucide-react";
import UserHeader from "../../components/UserHeader";
import Footer from "../../components/Footer";
import userStore from "../../zustand/userStore";
import { BASE_URL } from "../../configs/WebServiceUrl";
import { apiKeyHeader } from "../../configs/ApiKeys";
import { InitiatePaymentThunk } from "../../features/pricing/redux/thunks";
import { getStateNameFromGst, getStateCodeFromGst } from "../../utils/gstStateCodes";
import {
  INR,
  TUTOR_COMPLETE_PLAN,
  TUTOR_FEATURE_PRICE,
  TUTOR_FREE_FEATURE,
  TUTOR_PAID_FEATURES,
  TUTOR_PLAN_META,
  TUTOR_VALIDITY,
} from "./plans";
import "./pricing.css";

const MAX_USERS = 50;

// ── GST Popup — defined outside TutorPricing to prevent remount on re-render ──
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

function GSTPopup({
  open,
  gstChoice,
  gstNumber,
  gstError,
  onChoiceChange,
  onNumberChange,
  onSubmit,
  onCancel,
}) {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          padding: "32px 28px 28px",
          width: "100%",
          maxWidth: "440px",
          fontFamily: "inherit",
        }}
      >
        <h3 style={{ margin: "0 0 6px", fontSize: "1.2rem", fontWeight: 700, color: "#1a1a2e" }}>
          GST Details
        </h3>
        <p style={{ margin: "0 0 20px", color: "#555", fontSize: "0.93rem" }}>
          Do you want a GST bill?
        </p>

        {/* Radio buttons */}
        <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
          {["yes", "no"].map((val) => (
            <label
              key={val}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontSize: "0.97rem",
                fontWeight: gstChoice === val ? 600 : 400,
                color: gstChoice === val ? "#4f46e5" : "#333",
              }}
            >
              <input
                type="radio"
                name="gst_choice"
                value={val}
                checked={gstChoice === val}
                onChange={() => onChoiceChange(val)}
                style={{ accentColor: "#4f46e5", width: "16px", height: "16px" }}
              />
              {val === "yes" ? "Yes, I have GST" : "No, skip GST"}
            </label>
          ))}
        </div>

        {/* GST number input (visible only when "yes") */}
        {gstChoice === "yes" && (
          <div style={{ marginBottom: "18px" }}>
            <label
              htmlFor="gst_number_input"
              style={{
                display: "block",
                marginBottom: "6px",
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "#333",
              }}
            >
              Enter your GST Number
            </label>
            <input
              id="gst_number_input"
              type="text"
              maxLength={15}
              placeholder="e.g. 29ABCDE1234F1Z5"
              value={gstNumber}
              onChange={(e) => onNumberChange(e.target.value.toUpperCase())}
              autoFocus
              style={{
                width: "100%",
                padding: "10px 12px",
                border: gstError ? "1.5px solid #ef4444" : "1.5px solid #d1d5db",
                borderRadius: "7px",
                fontSize: "0.97rem",
                outline: "none",
                boxSizing: "border-box",
                letterSpacing: "0.05em",
                transition: "border 0.2s",
              }}
              onFocus={(e) => { e.target.style.borderColor = "#4f46e5"; }}
              onBlur={(e) => { e.target.style.borderColor = gstError ? "#ef4444" : "#d1d5db"; }}
            />
            {gstError && (
              <p style={{ color: "#ef4444", fontSize: "0.82rem", marginTop: "5px", marginBottom: 0 }}>
                {gstError}
              </p>
            )}
            {!gstError && gstNumber.trim().length >= 2 && (
              <p
                style={{
                  color: getStateNameFromGst(gstNumber) ? "#16a34a" : "#dc2626",
                  fontSize: "0.82rem",
                  marginTop: "5px",
                  marginBottom: 0,
                  fontWeight: 500,
                }}
              >
                {getStateNameFromGst(gstNumber)
                  ? `State: ${getStateNameFromGst(gstNumber)} (Code: ${getStateCodeFromGst(gstNumber)})`
                  : `Unknown state code: ${getStateCodeFromGst(gstNumber)}`}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "8px" }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "9px 20px",
              borderRadius: "7px",
              border: "1.5px solid #d1d5db",
              background: "#fff",
              color: "#555",
              fontSize: "0.93rem",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            style={{
              padding: "9px 24px",
              borderRadius: "7px",
              border: "none",
              background: "#4f46e5",
              color: "#fff",
              fontSize: "0.95rem",
              cursor: "pointer",
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            {gstChoice === "yes" ? "Submit & Pay" : "Proceed to Pay"}
          </button>
        </div>
      </div>
    </div>
  );
}
// ───────────────────────────────────────────────────────────────────────────────

function UsersField({ value, onChange }) {
  return (
    <div className="pl-field">
      <span className="pl-coupon-label">Number of Users</span>
      <div className="pl-stepper">
        <button
          type="button"
          className="pl-step-btn"
          onClick={() => onChange(Math.max(1, value - 1))}
          disabled={value <= 1}
          aria-label="One fewer user"
        >
          <Minus size={16} />
        </button>
        <span className="pl-step-value" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          className="pl-step-btn"
          onClick={() => onChange(Math.min(MAX_USERS, value + 1))}
          disabled={value >= MAX_USERS}
          aria-label="One more user"
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
}

function CouponField({ id, code, setCode, applied, onApply, onRemove }) {
  return (
    <form className="pl-field" onSubmit={onApply}>
      <label className="pl-coupon-label" htmlFor={id}>
        Coupon
      </label>
      <div className="pl-coupon-row">
        <input
          id={id}
          className="pl-coupon-input"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="Enter coupon code"
          autoComplete="off"
        />
        <button type="submit" className="dw-btn">
          Apply
        </button>
      </div>
      {applied ? (
        <p className="pl-coupon-msg is-ok">
          {applied.code} applied ({applied.discount} off).{" "}
          <button type="button" className="pl-linkbtn" onClick={onRemove}>
            Remove
          </button>
        </p>
      ) : null}
    </form>
  );
}

export default function TutorPricing({ embedded = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = userStore((state) => state.user);

  const [apiModels, setApiModels] = useState([]);
  const [completeUsers, setCompleteUsers] = useState(1);
  const [customUsers, setCustomUsers] = useState(1);
  const [selected, setSelected] = useState(() => new Set());

  const [completeCouponCode, setCompleteCouponCode] = useState("");
  const [completeAppliedCoupon, setCompleteAppliedCoupon] = useState(null);
  const [completeCouponId, setCompleteCouponId] = useState(null);

  const [customCouponCode, setCustomCouponCode] = useState("");
  const [customAppliedCoupon, setCustomAppliedCoupon] = useState(null);
  const [customCouponId, setCustomCouponId] = useState(null);

  // ── GST Popup state ────────────────────────────────────────────────────────
  const [gstPopupOpen, setGstPopupOpen] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [gstChoice, setGstChoice] = useState("no");
  const [gstNumber, setGstNumber] = useState("");
  const [gstError, setGstError] = useState("");
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchCustomizedModels = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/subscriptionmodel/customized`
        );
        const data = Array.isArray(response.data) ? response.data : [];
        setApiModels(data);
      } catch (err) {
        console.error("Error fetching customized models:", err);
      }
    };
    fetchCustomizedModels();
  }, []);

  const calcDiscount = (subtotal, appliedCoupon) => {
    if (!appliedCoupon || subtotal === 0) return 0;
    const raw = appliedCoupon.discount;
    let disc = 0;
    if (typeof raw === "string" && raw.includes("%")) {
      const pct = parseFloat(raw.replace("%", ""));
      disc = Math.round((subtotal * pct) / 100);
    } else {
      disc = parseFloat(raw) || 0;
    }
    return Math.min(disc, subtotal > 1 ? subtotal - 1 : subtotal);
  };

  const complete = useMemo(() => {
    const perUser = TUTOR_COMPLETE_PLAN.perUser;
    const subtotal = perUser * Math.max(1, completeUsers);
    const discount = calcDiscount(subtotal, completeAppliedCoupon);
    const total = Math.max(1, subtotal - discount);
    return { perUser, subtotal, discount, total };
  }, [completeUsers, completeAppliedCoupon]);

  const custom = useMemo(() => {
    const count = selected.size;
    const perUser = count * TUTOR_FEATURE_PRICE;
    const subtotal = perUser * Math.max(1, customUsers);
    const discount = calcDiscount(subtotal, customAppliedCoupon);
    const total = Math.max(1, subtotal - discount);
    return { perUser, subtotal, discount, total };
  }, [selected.size, customUsers, customAppliedCoupon]);

  const toggleFeature = (id) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const seats = (n) => `${n} user${n === 1 ? "" : "s"}`;

  const handleCompleteApplyCoupon = async (e) => {
    if (e) e.preventDefault();
    if (!completeCouponCode.trim()) return alert("Please enter a coupon code.");

    if (completeCouponCode.trim().toUpperCase() === "WELCOME20") {
      if (!userData?.created) return alert("Your coupon code has expired.");
      const minutes = (new Date() - new Date(userData.created)) / (1000 * 60);
      if (minutes > 15) {
        return alert("Your coupon code has expired. It was valid only within 15 minutes of registration.");
      }
    }

    try {
      const body = {
        code: completeCouponCode.trim(),
        plan_types: ["24"],
        school_id: userData?.admin_id,
      };

      const res = await axios.post(
        "https://web1024.docmaster.in:5010/api/coupons/validate",
        body,
        {
          headers: {
            ...apiKeyHeader(),
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
        }
      );

      const data = res?.data;
      if (!data?.success) return alert(data?.message || "Invalid coupon");

      setCompleteCouponId(data.id);
      setCompleteAppliedCoupon({
        code: completeCouponCode.trim(),
        discount: data.discount || 0,
      });
    } catch (error) {
      alert("Error validating coupon");
    }
  };

  const handleCompleteRemoveCoupon = () => {
    setCompleteCouponCode("");
    setCompleteAppliedCoupon(null);
    setCompleteCouponId(null);
  };

  const handleCustomApplyCoupon = async (e) => {
    if (e) e.preventDefault();
    if (!customCouponCode.trim()) return alert("Please enter a coupon code.");
    if (selected.size === 0) return alert("Please select a feature first.");

    if (customCouponCode.trim().toUpperCase() === "WELCOME20") {
      if (!userData?.created) return alert("Your coupon code has expired.");
      const minutes = (new Date() - new Date(userData.created)) / (1000 * 60);
      if (minutes > 15) {
        return alert("Your coupon code has expired. It was valid only within 15 minutes of registration.");
      }
    }

    const featureCount = selected.size;
    let modelId = 9;
    if (featureCount === 1) modelId = 9;
    else if (featureCount === 2) modelId = 10;
    else if (featureCount === 3) modelId = 11;
    else if (featureCount === 4) modelId = 12;

    try {
      const body = {
        code: customCouponCode.trim(),
        plan_types: [String(modelId)],
        school_id: userData?.admin_id,
      };

      const res = await axios.post(
        "https://web1024.docmaster.in:5010/api/coupons/validate",
        body,
        {
          headers: {
            ...apiKeyHeader(),
            "Content-Type": "application/json",
            "x-tenant-db": "main_admin_db",
          },
        }
      );

      const data = res?.data;
      if (!data?.success) return alert(data?.message || "Invalid coupon");

      setCustomCouponId(data.id);
      setCustomAppliedCoupon({
        code: customCouponCode.trim(),
        discount: data.discount || 0,
      });
    } catch (error) {
      alert("Error validating coupon");
    }
  };

  const handleCustomRemoveCoupon = () => {
    setCustomCouponCode("");
    setCustomAppliedCoupon(null);
    setCustomCouponId(null);
  };

  const createCouponLink = async (couponId) => {
    if (!couponId) return null;
    const schoolId = userData?.admin_id;
    try {
      const response = await axios.post(
        "https://web1024.docmaster.in:5010/api/coupons/createCouponLink",
        { schoolId, couponId, createdById: schoolId, status: "incompleted" },
        { headers: { "Content-Type": "application/json", "x-tenant-db": "main_admin_db" } }
      );
      if (response && (response.status === 200 || response.status === 201)) {
        return response.data?.insertId || null;
      }
      return null;
    } catch (error) {
      console.error("Error creating coupon link:", error);
      return null;
    }
  };

  // ── GST Popup handlers ──────────────────────────────────────────────────
  const handlePaymentClick = (type) => {
    setSelectedPaymentType(type);
    setGstChoice("no");
    setGstNumber("");
    setGstError("");
    setGstPopupOpen(true);
  };

  const handleGstChoiceChange = (val) => {
    setGstChoice(val);
    setGstError("");
  };

  const handleGstNumberChange = (val) => {
    setGstNumber(val);
    setGstError("");
  };

  const handleGstSubmit = () => {
    let formattedGst = "";
    let stateName = "";
    let stateCode = "";

    if (gstChoice === "yes") {
      formattedGst = gstNumber.trim().toUpperCase();
      if (!formattedGst) {
        setGstError("GST number is required.");
        return;
      }
      if (!GST_REGEX.test(formattedGst)) {
        setGstError("Enter a valid 15-character Indian GST number.");
        return;
      }
      stateName = getStateNameFromGst(formattedGst);
      stateCode = getStateCodeFromGst(formattedGst);
      if (!stateName) {
        setGstError(`Invalid state code (${stateCode}) in GST number.`);
        return;
      }
    }

    setGstPopupOpen(false);
    handlePayment(selectedPaymentType, {
      is_gst: gstChoice === "yes",
      gst_number: formattedGst,
      state: stateName,
      state_name: stateName,
      state_code: stateCode,
    });
  };

  const handleGstCancel = () => {
    setGstPopupOpen(false);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handlePayment = async (type, gstData = {}) => {
    if (!userData) {
      alert("Please login to continue.");
      return;
    }

    const isComplete = type === "complete";
    const usersCount = isComplete ? completeUsers : customUsers;
    const quote = isComplete ? complete : custom;
    const couponId = isComplete ? completeCouponId : customCouponId;
    const appliedCoupon = isComplete ? completeAppliedCoupon : customAppliedCoupon;

    const linkData = await createCouponLink(couponId);

    let modelId = null;
    let moduleIds = [];

    if (isComplete) {
      modelId = 24;
      moduleIds = [2, 3, 5, 6];
    } else {
      const featureCount = selected.size;
      if (featureCount === 1) modelId = 9;
      else if (featureCount === 2) modelId = 10;
      else if (featureCount === 3) modelId = 11;
      else if (featureCount === 4) modelId = 12;

      const moduleMap = {
        question_papers: 2,
        timetable: 3,
        teacher_planner: 5,
        documents: 6,
      };

      moduleIds = [
        ...Array.from(selected).map((id) => moduleMap[id] || id),
        6,
      ];
    }

    const gstPayload = {
      is_gst: Boolean(gstData?.is_gst),
      gst_number: gstData?.gst_number || "",
      state_name: gstData?.state_name || "",
      state_code: gstData?.state_code || "",
    };

    // Calculate discount percentage if any
    let discountPct = 0;
    if (appliedCoupon && appliedCoupon.discount) {
      const raw = appliedCoupon.discount;
      if (typeof raw === "string" && raw.includes("%")) {
        discountPct = parseFloat(raw.replace("%", "")) || 0;
      } else if (quote.subtotal > 0 && quote.discount > 0) {
        discountPct = parseFloat(((quote.discount / quote.subtotal) * 100).toFixed(2));
      }
    }

    const planName = isComplete
      ? TUTOR_COMPLETE_PLAN.name
      : `Custom Plan (${selected.size} feature${selected.size === 1 ? "" : "s"})`;

    const planDetailsArray = [
      {
        plan_name: planName,
        model_id: modelId,
        actual_amount: quote.subtotal,
        discount_amount: quote.discount,
        discount_percentage: discountPct,
        final_amount: quote.total,
        total_users: usersCount,
        coupon_code: appliedCoupon?.code || "",
      },
    ];

    const body = {
      txnRefNo: Date.now(),
      amount: quote.total,
      txnType: "Pay",
      orderInfo: String(modelId || ""),
      email: userData?.email || "",
      firstName: userData?.admin_name || userData?.name || "",
      lastName: "",
      street: "Delhi",
      city: "",
      state: gstData?.state || "",
      zip: 506002,
      phone: userData?.contact_number || 0,
      udf21: userData?.admin_id || userData?.id,
      udf22: userData?.id,
      udf23: moduleIds.join(","),
      udf24: "customized",
      udf25: usersCount,
      udf26: linkData || "",
      udf27: String(modelId || ""),
      udf28: JSON.stringify(gstPayload),
      udf29: JSON.stringify(planDetailsArray),
    };

    console.log("Tutor Plan Payment Body:", body);
    dispatch(InitiatePaymentThunk(body));
  };

  const nothingChosen = selected.size === 0;

  return (
    <div className="dw-app">
      {/* GST Popup */}
      <GSTPopup
        open={gstPopupOpen}
        gstChoice={gstChoice}
        gstNumber={gstNumber}
        gstError={gstError}
        onChoiceChange={handleGstChoiceChange}
        onNumberChange={handleGstNumberChange}
        onSubmit={handleGstSubmit}
        onCancel={handleGstCancel}
      />

      {!embedded && <UserHeader />}

      {!embedded && (
        <section className="pl-hero">
          <h1 className="pl-hero-title">Individual Tutor Plans</h1>
          <p className="pl-hero-sub">Choose Your Features</p>
        </section>
      )}

      <main className={`pl-page${embedded ? "" : " has-hero"}`}>
        <div className="pl-twoplans">
          {/* Card 1: everything, one price. */}
          <section className="pl-card pl-card-plain pl-plan">
            <header className="pl-plan-head">
              <div className="pl-plan-titlerow">
                <p className="pl-card-name">{TUTOR_COMPLETE_PLAN.name}</p>
                <span className="pl-plan-titlenote">
                  Includes all 4 Features for {TUTOR_VALIDITY}
                </span>
              </div>

              <div className="pl-price">
                {INR(TUTOR_COMPLETE_PLAN.perUser)}
                <span className="pl-price-unit">/user</span>
              </div>
              <p className="pl-storage">{TUTOR_COMPLETE_PLAN.storage} storage</p>
            </header>

            <div className="pl-plan-body">
              <ul className="pl-features">
                {TUTOR_COMPLETE_PLAN.features.map((feature) => (
                  <li key={feature} className="pl-feature">
                    <Check size={17} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div className="pl-plan-foot">
              <UsersField value={completeUsers} onChange={setCompleteUsers} />
              <CouponField
                id="pl-coupon-complete"
                code={completeCouponCode}
                setCode={setCompleteCouponCode}
                applied={completeAppliedCoupon}
                onApply={handleCompleteApplyCoupon}
                onRemove={handleCompleteRemoveCoupon}
              />

              <div className="pl-totals">
                <div className="pl-details-row">
                  <span>
                    {INR(TUTOR_COMPLETE_PLAN.perUser)} × {seats(completeUsers)}
                  </span>
                  <strong>{INR(complete.subtotal)}</strong>
                </div>
                {complete.discount > 0 ? (
                  <div className="pl-details-row is-discount">
                    <span>Coupon {completeAppliedCoupon?.code}</span>
                    <strong>−{INR(complete.discount)}</strong>
                  </div>
                ) : null}
              </div>

              <div className="pl-total">
                <span className="pl-total-label">Total Amount</span>
                <span className="pl-total-value">{INR(complete.total)}</span>
              </div>

              <button
                type="button"
                className="dw-btn dw-btn-primary pl-btn-block pl-paybtn"
                onClick={() => handlePaymentClick("complete")}
              >
                Pay Now {INR(complete.total)}
              </button>
            </div>
          </section>

          {/* Card 2: pay only for what you use. */}
          <section className="pl-card pl-card-plain pl-plan">
            <header className="pl-plan-head">
              <div className="pl-plan-titlerow">
                <p className="pl-card-name">Choose Your Features</p>
                <span className="pl-plan-titlenote">
                  {INR(TUTOR_FEATURE_PRICE)}/user for {TUTOR_VALIDITY}
                </span>
              </div>
              <p className="pl-plan-lead">Pick as many features as you need.</p>
            </header>

            <div className="pl-plan-body">
              <div className="pl-featuregrid">
                {TUTOR_PAID_FEATURES.map((feature) => {
                  const on = selected.has(feature.id);
                  return (
                    <button
                      key={feature.id}
                      type="button"
                      className={`pl-feature-card${on ? " is-on" : ""}`}
                      style={{ "--f": feature.color }}
                      onClick={() => toggleFeature(feature.id)}
                      aria-pressed={on}
                    >
                      <span className="pl-feature-check" aria-hidden="true">
                        {on ? <Check size={14} strokeWidth={3} /> : null}
                      </span>
                      <img
                        className="pl-feature-icon"
                        src={feature.icon}
                        alt=""
                        aria-hidden="true"
                      />
                      <span className="pl-feature-name">{feature.name}</span>
                      <span className="pl-feature-price">
                        {INR(TUTOR_FEATURE_PRICE)}/user
                      </span>
                      <span className="pl-feature-usage">{feature.usage}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pl-plan-foot">
              <UsersField value={customUsers} onChange={setCustomUsers} />
              <CouponField
                id="pl-coupon-custom"
                code={customCouponCode}
                setCode={setCustomCouponCode}
                applied={customAppliedCoupon}
                onApply={handleCustomApplyCoupon}
                onRemove={handleCustomRemoveCoupon}
              />

              <div className="pl-totals">
                <div className="pl-details-row">
                  <span>
                    {selected.size} feature{selected.size === 1 ? "" : "s"} ×{" "}
                    {INR(TUTOR_FEATURE_PRICE)} × {seats(customUsers)}
                  </span>
                  <strong>{INR(custom.subtotal)}</strong>
                </div>
                <div className="pl-details-row">
                  <span>Per user</span>
                  <strong>{INR(custom.perUser)}</strong>
                </div>
                {custom.discount > 0 ? (
                  <div className="pl-details-row is-discount">
                    <span>Coupon {customAppliedCoupon?.code}</span>
                    <strong>−{INR(custom.discount)}</strong>
                  </div>
                ) : null}
              </div>

              <div className="pl-total">
                <span className="pl-total-label">Total Amount</span>
                <span className="pl-total-value">{INR(custom.total)}</span>
              </div>

              <button
                type="button"
                className="dw-btn dw-btn-primary pl-btn-block pl-paybtn"
                onClick={() => handlePaymentClick("custom")}
                disabled={nothingChosen}
              >
                {nothingChosen
                  ? "Select a feature to continue"
                  : `Pay Now ${INR(custom.total)}`}
              </button>
            </div>
          </section>
        </div>

        <section
          className="pl-card pl-card-plain pl-freecard"
          style={{ "--f": TUTOR_FREE_FEATURE.color }}
        >
          <img
            className="pl-feature-icon"
            src={TUTOR_FREE_FEATURE.icon}
            alt=""
            aria-hidden="true"
          />
          <div className="pl-freecard-text">
            <p className="pl-freecard-name">{TUTOR_FREE_FEATURE.name}</p>
            <p className="pl-freecard-note">
              Always included on either plan. It doesn't add anything to your total.
            </p>
          </div>
          <span className="dw-chip is-ok pl-freecard-tag">FREE</span>
        </section>
      </main>

      {!embedded && <Footer userData={userData} />}
    </div>
  );
}
